import { pbkdf2 } from "pbkdf2";
import Cookies from "js-cookie";
import { CONFIG } from "./config";

function pbkdf2Async(password, salt, iterations, keylen, digest) {
  return new Promise((res, rej) => {
    pbkdf2(password, salt, iterations, keylen, digest, (err, key) => {
      err ? rej(err) : res(key);
    });
  });
}

export function formatUrlName(name) {
  return name
    .trim() // Remove leading and trailing spaces
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .toLowerCase(); // Convert to lowercase
}

export const formatName = (name) => {
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

export async function generateHash(dataToHash) {
  const salt = CONFIG.hashKey || "";
  try {
    const hash = await pbkdf2Async(
      JSON.stringify(dataToHash),
      salt,
      10000,
      100,
      "sha512",
    );
    return hash.toString("hex");
  } catch (error) {
    return "";
  }
}

export function makeid(length) {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
}

function generateUniqueBrowserIdentifier() {
  const randomPart = ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(
    /[018]/g,
    (c) =>
      (
        c ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
      ).toString(16),
  );
  const timestamp = Date.now();
  return `${randomPart}-${timestamp}`;
}

export function getIdentifier() {
  let identifier = localStorage.getItem("identifier");
  if (!identifier) {
    identifier = generateUniqueBrowserIdentifier();
    localStorage.setItem("identifier", identifier);
  }
  Cookies.set("identifier", identifier, {});
  return identifier;
}

function removeNonAlphanumericExceptAmpersand(str) {
  return str.replace(/[^a-zA-Z0-9& ]/g, "");
}

export function convertTitleToURLPath(title) {
  title = removeNonAlphanumericExceptAmpersand(title);
  // Remove leading and trailing spaces (if any)
  const trimmedTitle = title.trim();
  // Replace spaces with dashes to create the URL path
  const urlPath = trimmedTitle.replace(/\s+/g, "-");

  return urlPath;
}

export function daysToReachTargetDate(targetDate) {
  // Get the current date
  const currentDate = new Date();

  // Convert the target date string to a Date object
  const targetDateObj = new Date(targetDate);

  // Calculate the time difference in milliseconds
  const timeDifference = targetDateObj - currentDate;

  // Convert milliseconds to days
  const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

  return daysDifference;
}

export function generateMetaData(metaData) {
  const userAgent = window.navigator.userAgent;
  const os = getOS();
  return {
    app: "web",
    metaData: {
      ipAddress: metaData.query,
      country: metaData.country,
      countryCode: metaData.countryCode,
      userAgent: userAgent,
      latitude: metaData.lat,
      longitude: metaData.lon,
      city: metaData.regionName,
      timezone: metaData.timezone,
      deviceName: window.navigator.platform,
      os: os,
      deviceNo: "",
    },
  };
}

export function generateMetaDataServerSide(metaData) {
  return {
    app: "web",
    metaData: {
      ipAddress: metaData?.query,
      country: metaData?.country,
      countryCode: metaData?.countryCode,
      userAgent: "ds",
      latitude: metaData?.lat,
      longitude: metaData?.lon,
      city: metaData?.regionName,
      timezone: metaData?.timezone,
      deviceName: "ds",
      os: "ANDROID",
      deviceNo: "",
    },
  };
}

function getOS() {
  var userAgent = window.navigator.userAgent,
    platform = window.navigator.platform,
    macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K"],
    windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"],
    iosPlatforms = ["iPhone", "iPad", "iPod"],
    os = null;

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = "MAC";
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = "IOS";
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = "WINDOWS";
  } else if (/Android/.test(userAgent)) {
    os = "ANDROID";
  } else if (!os && /Linux/.test(platform)) {
    os = "OTHERS";
  } else {
    os = null;
  }
  return os;
}

export const getInitials = function (name, maxCharter = null) {
  if (!name || typeof name !== "string") return "";

  // Split by spaces, trim, and remove empties
  const parts = name.trim().split(/\s+/).filter(Boolean);

  // Build initials from first character of each part
  let initials = parts
    .map((p) => p[0])
    .join("")
    .toUpperCase();

  // Limit length if maxCharter is provided
  if (maxCharter && Number.isInteger(maxCharter) && maxCharter > 0) {
    initials = initials.slice(0, maxCharter);
  }

  return initials;
};

export const getBoxSize = (
  width,
  height,
  params,
  align = "r",
  target = 500,
) => {
  let { x_max_size, y_max_size, y_min_size, x_min_size } = params;

  let larger_dim = "x";
  let larger_curr_value = null;
  let larger_dim_value = null;

  let x_check = parseFloat(width) - parseFloat(x_max_size);
  let y_check = parseFloat(height) - parseFloat(y_max_size);

  if (x_check < y_check) {
    larger_dim = "y";
    larger_curr_value = parseFloat(height);
    larger_dim_value = parseFloat(y_max_size);
  } else {
    larger_dim = "x";
    larger_curr_value = parseFloat(width);
    larger_dim_value = parseFloat(x_max_size);
  }

  let tn_width = width;
  let tn_height = height;

  if (align == "r") {
    const target_size = parseInt(larger_dim_value);
    if (larger_curr_value > larger_dim_value) {
      if (larger_dim == "x") {
        tn_width = Math.floor(target_size);
        tn_height = Math.floor((height / width) * target_size);
      } else {
        tn_height = Math.floor(target_size);
        tn_width = Math.floor((width / height) * target_size);
      }
    } else {
      tn_width = width;
      tn_height = height;
    }
    if (y_min_size && x_min_size) {
      if (tn_height < y_min_size) {
        tn_height = Math.floor(y_min_size);
        tn_width = Math.floor((width / height) * y_min_size);
      }

      if (tn_width < x_min_size) {
        tn_width = Math.floor(x_min_size);
        tn_height = Math.floor((height / width) * x_min_size);
      }
    }
  } else if (align == "w") {
    tn_height = Math.floor((target * height) / width);
    tn_width = Math.floor(target);
  } else if (align == "h") {
    tn_width = Math.floor((target * width) / height);
    tn_height = Math.floor(target);
  }

  return {
    width: tn_width,
    height: tn_height,
  };
};

export function generateAbbreviation(name = "") {
  const words = name.split(/\s+/).filter(Boolean);
  const letters = [];

  for (const word of words) {
    // Find first alphabetical character in the word
    const match = word.match(/[A-Za-z]/);
    if (match) {
      letters.push(match[0].toUpperCase());
    }

    // Stop once we have 2 letters
    if (letters.length === 2) break;
  }

  return letters.join("");
}

export function extractInfoFromUrl(query) {
  const search = window.location.search;

  const params = new URLSearchParams(search);

  const value = params.get(query);

  return value;
}

export const getStorage = (storageKey) => {
  try {
    if (typeof window === "undefined") return null;
    const item = localStorage.getItem(storageKey);
    if (item) {
      return JSON.parse(item);
    }
  } catch (error) {
    console.error(error);
  }
  return null;
};

export const setStorage = (storageKey, data) => {
  if (typeof window === "undefined") return null;
  try {
    localStorage.setItem(storageKey, JSON.stringify(data));
  } catch (error) {
    console.error(error);
  }
};

export const unsetStorage = (storageKey) => {
  if (typeof window === "undefined") return null;
  try {
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error(error);
  }
};

export const clearStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.clear();
  } catch (error) {
    console.error(error);
  }
};

export const setLoggedinUser = (user) => {
  return setStorage("__user", user);
};

export const getLoggedinUser = () => {
  return getStorage("__user");
};

export const formatChatDate = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isToday =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  if (isToday) return "Today";
  if (isYesterday) return "Yesterday";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }); // DD/MM/YYYY
};

export const normalizeName = (str = "") => {
  return decodeURIComponent(str || "") // turn %20 into spaces
    .toLowerCase() // lowercase everything
    .replace(/\s+/g, "-") // spaces → hyphens
    .replace(/[^a-z0-9\-']/g, "") // ❗ allow apostrophes
    .replace(/-+/g, "-") // collapse multiple hyphens
    .replace(/^-|-$/g, ""); // remove hyphens at ends
};

export const generateProductUrl = (storePath, product) => {
  // Clean + slugify product name
  const slug = product.name
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, "") // remove special characters
    .replace(/\s+/g, "-") // spaces → dashes
    .replace(/-+/g, "-") // collapse multiple dashes
    .replace(/^-+|-+$/g, ""); // avoid double separators before checksum

  // Normalize storePath (remove trailing /)
  const base = storePath === "/" ? "" : storePath.replace(/\/+$/, "");
  const checkSum = encodeProductChecksumForUrl(product.checkSum);

  return `${base}${base ? "/" : ""}product/${slug}-${checkSum}`;
};

export function encodeProductChecksumForUrl(checkSum = "") {
  return String(checkSum).replace(/-/g, "--");
}

export function extractProductChecksumFromSlug(slug = "") {
  const segment = String(slug || "");
  const decodedSegment = decodeURIComponent(segment);

  for (let index = decodedSegment.length - 1; index >= 0; index -= 1) {
    if (decodedSegment[index] !== "-") continue;

    const isEscapedChecksumHyphen =
      decodedSegment[index - 1] === "-" || decodedSegment[index + 1] === "-";

    if (!isEscapedChecksumHyphen) {
      const extracted = decodedSegment.slice(index + 1).replace(/--/g, "-");
      const trailingChecksumMatch = decodedSegment.match(
        /--([A-Z0-9]*[A-Z][A-Z0-9]*\d[A-Z0-9]*)$/,
      );
      const extractedPrefix = extracted.split("-").slice(0, -1).join("-");

      if (trailingChecksumMatch?.[1] && /[a-z]/.test(extractedPrefix)) {
        return trailingChecksumMatch[1];
      }

      return extracted;
    }
  }

  return "";
}

export const knownPaths = [
  "auth",
  "category",
  "product",
  "checkout",
  "profile",
  "home",
  "market-place",
  "stores",
];

export const knownMarketplacePaths = [
  "stores",
  "category",
  "checkout",
  "auth",
  "home",
  "profile",
];

export const getUrlPathFromPathname = (pathname) => {
  const lowerKnownPaths = knownPaths.map((p) => p.toLowerCase());
  const normalized = pathname.toLowerCase();
  const parts = normalized.split("/").filter(Boolean);

  // ✅ Root path → home
  if (normalized === "/") {
    return "home";
  }

  // ✅ market-place + subpaths (checkout, orders, etc.)
  if (normalized.startsWith("/market-place")) {
    const after = parts.slice(1); // everything after "market-place"

    const match = after.find((seg) => lowerKnownPaths.includes(seg));
    if (match) {
      return match;
    }

    return "market-place";
  }

  // ✅ /store/:handle/... pattern
  const storeIndex = parts.findIndex((p) => p === "store");
  if (storeIndex !== -1 && parts.length > storeIndex + 1) {
    const afterHandle = parts.slice(storeIndex + 2); // skip "store" + handle

    if (afterHandle.length === 0) return "home";

    // find first known path in the rest
    const match = afterHandle.find((seg) => lowerKnownPaths.includes(seg));
    if (match) return match;

    return afterHandle.length === 1 ? "home" : "unknown";
  }

  // ✅ Subdomain style (/auth, /product, /lagos/product, etc.)
  if (parts.length > 0) {
    const match = parts.find((seg) => lowerKnownPaths.includes(seg));
    if (match) return match;

    // if only location → home
    if (parts.length === 1) return "home";
  }

  return "unknown";
};

const KNOWN_PATHS = new Set(knownPaths);

function cleanPath(pathname) {
  return pathname.split("?")[0].split("#")[0];
}

export function getStorePath(pathname) {
  const clean = cleanPath(pathname);
  const segments = clean.split("/").filter(Boolean);

  if (!segments.length) return "";

  // ✅ special-case marketplace
  if (segments[0] === "market-place") {
    return "market-place";
  }

  const storeSegments = [];

  for (const segment of segments) {
    if (KNOWN_PATHS.has(segment)) break;
    storeSegments.push(segment);
  }

  return storeSegments.join("/");
}

export function getBasePath(pathname) {
  const clean = cleanPath(pathname);
  const segments = clean.split("/").filter(Boolean);

  // market-place or anything under it
  if (segments[0] === "market-place") {
    return "market-place";
  }

  // store/<brand>/...
  if (segments[0] === "store" && segments[1]) {
    return "store/" + segments[1];
  }

  return "";
}

export function extractChecksumFromPath(pathname) {
  return extractProductChecksumFromSlug(pathname.split("/").pop());
}

export const buildQueryString = (params = {}, mainQuery = true) => {
  let finalParams = {};

  if (mainQuery && typeof window !== "undefined") {
    // Parse current query params
    const currentParams = new URLSearchParams(window.location.search);
    for (const [key, value] of currentParams.entries()) {
      finalParams[key] = value;
    }
  }

  // Merge or override with new params
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === "" || value === null) {
      // If you want to completely remove a param when value is empty, delete it
      delete finalParams[key];
    } else {
      finalParams[key] = value;
    }
  });

  // Build query string
  const query = Object.entries(finalParams)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");

  return query ? `?${query}` : "";
};

export const toUrlSlug = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const getCategoryUrlPath = (storePath = "", categoryName = "") => {
  const categorySlug = toUrlSlug(categoryName);
  if (!categorySlug) return storePath || "home";

  const base = String(storePath || "").replace(/\/+$/, "");
  return base ? `${base}/category/${categorySlug}` : `category/${categorySlug}`;
};

export const getCategorySlugFromPath = (path = "") => {
  const segments = String(path).split("?")[0].split("#")[0].split("/");
  const categoryIndex = segments.findIndex(
    (segment) => segment.toLowerCase() === "category",
  );

  return categoryIndex >= 0 ? segments[categoryIndex + 1] || "" : "";
};

export const isTokenValid = (token, expiry) => {
  if (!token || !expiry) return false;
  const expiryDate = new Date(expiry);
  return expiryDate > new Date();
};

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function generateObjectId() {
  const timestamp = Math.floor(new Date().getTime() / 1000).toString(16);
  return (
    timestamp +
    "xxxxxxxxxxxxxxxx" // 16 random hex chars
      .replace(/[x]/g, () => ((Math.random() * 16) | 0).toString(16))
  ).toLowerCase();
}

export function isValidPath(first, knownPaths, accessPoints) {
  const lowerKnownPaths = knownPaths.map((p) => p.toLowerCase());
  const isKnownPath = first ? lowerKnownPaths.includes(first) : true;
  const isAccessPoint = first
    ? accessPoints.some(
        (ap) => ap.name.replace(/\s+/g, "-").toLowerCase() === first,
      )
    : true;

  return isKnownPath || isAccessPoint;
}

/**
 * Parse slug into urlPath, location, productNo, fullUrlPath, firstSegment, and lastSegment
 * @param {string} handle - the store handle
 * @param {string[]|string} slugParam - the slug array or string
 */
export const parseStoreSlug = (handle, slugParam) => {
  const slug = Array.isArray(slugParam) ? slugParam : [];
  const fullUrlPath = slug.join("/");

  // detect urlPath (home, product, auth, etc.)
  const urlPath = getUrlPathFromPathname(`/store/${handle}/${fullUrlPath}`);

  // detect location (first segment if not a known path)
  const guessLocation = slug[0];
  const location =
    guessLocation &&
    !knownPaths.some((p) => p.toLowerCase() === guessLocation.toLowerCase())
      ? guessLocation
      : undefined;

  // detect product number from slug if urlPath = product
  let productNo = null;
  if (urlPath === "product") {
    const productIndex = slug.indexOf("product");
    const productSegment = slug[productIndex + 1];
    if (productSegment) {
      productNo = extractProductChecksumFromSlug(productSegment);
    }
  }
  let categorySlug = null;
  if (urlPath === "category") {
    const categoryIndex = slug.indexOf("category");
    categorySlug = slug[categoryIndex + 1] || null;
  }

  // ✅ first and last segments
  const firstSegment = slug.length > 0 ? slug[0] : undefined;
  const lastSegment = slug.length > 0 ? slug[slug.length - 1] : undefined;

  return {
    urlPath,
    location,
    productNo,
    categorySlug,
    fullUrlPath,
    firstSegment,
    lastSegment,
    knownPaths,
  };
};

export const parseMarketplaceSlug = (slugParam = []) => {
  const slug = Array.isArray(slugParam) ? slugParam : [];
  const fullUrlPath = slug.join("/");

  const firstSegment = slug[0]?.toLowerCase();

  const urlPath = firstSegment ?? "market-place";

  const isKnownPath =
    !firstSegment || knownMarketplacePaths.includes(firstSegment);

  return {
    urlPath,
    fullUrlPath,
    firstSegment,
    isKnownPath,
    knownMarketplacePaths,
  };
};

export function getDomainObject(headersList) {
  // Prefer forwarded headers since we often sit behind a proxy/load balancer
  const forwardedProto = headersList.get("x-forwarded-proto");
  const forwardedHost = headersList.get("x-forwarded-host");

  const protocol = (forwardedProto || "http").split(",")[0].trim();
  const rawHost = forwardedHost || headersList.get("host") || "localhost:3000";

  // Use the first host if multiple are provided (comma-separated)
  const normalizedHost = rawHost.split(",")[0].trim().toLowerCase();
  // Remove anything outside the allowed host characters
  const sanitizedHost = normalizedHost.replace(/[^a-z0-9._:-]/g, "");
  const host = sanitizedHost || "localhost:3000";
  return {
    fullDomain: `${protocol}://${host}`,
    domain: host,
  };
}

export function classifyDomain(domainInfo) {
  const { domain } = domainInfo;
  const cleanDomain = domain.split(":")[0].toLowerCase();

  // ✅ Home domains
  if (
    cleanDomain === "circleplug.com" ||
    cleanDomain === "stage.circleplug.com" ||
    cleanDomain === "www.circleplug.com" ||
    cleanDomain === "localhost"
  ) {
    const env =
      cleanDomain === "localhost"
        ? "local"
        : cleanDomain.includes("stage.")
          ? "stage"
          : "prod";
    return { type: "home", env };
  }

  if (
    cleanDomain === "circleplug.shop" ||
    cleanDomain === "stage.circleplug.shop" ||
    cleanDomain === "www.circleplug.shop"
  ) {
    // "stage.circleplug.shop" is treated as home but with stage env
    const env = cleanDomain.includes("stage.") ? "stage" : "prod";
    return { type: "home", env };
  }

  // ✅ Subdomain stores (stage first so it doesn't fall through)
  if (cleanDomain.endsWith(".stage.circleplug.shop")) {
    const subdomain = cleanDomain.replace(".stage.circleplug.shop", "");
    return { type: "store", handle: subdomain, env: "stage" };
  }

  if (cleanDomain.endsWith(".circleplug.shop")) {
    const subdomain = cleanDomain.replace(".circleplug.shop", "");
    return { type: "store", handle: subdomain, env: "prod" };
  }

  // ✅ External domains
  return { type: "external", domain: cleanDomain.replace(/^www\./, "") };
}

export const getPageClassification = (domainInfo) => {
  const classification = classifyDomain(domainInfo);
  // const classification = { type: "store", handle: "shypes", env: "prod" };
  // const classification = { type: "store", handle: "oaluxury", env: "prod" };
  // const classification = { type: "store", handle: "zellers", env: "prod" };
  // const classification = { type: "store", handle: "feetnshoulders", env: "prod" };
  // const classification = { type: "store", handle: "bmpromakeup", env: "prod" };
  // const classification = { type: "store", handle: "fragrancebyhoneydrop", env: "prod" };
  return classification;
};

export function generateHomePageMetadata() {
  const title =
    "CirclePlug | Everything your community needs, all in one place";
  const description =
    "Forget juggling multiple platforms! Build, engage, and monetize your thriving community – all within one seamless platform.";

  return {
    title,
    description,
    openGraph: {
      title,
      description,
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
    },
  };
}

export const getImageUrlWithResolution = (imageUrl = "", resolution = "r50x50") => {
  const url = String(imageUrl || "").trim();
  if (!url) return "";

  return url.replace(/r\d+x\d+/, resolution);
};

const getStoreIconMetadata = (organisation) => {
  const faviconUrl = getImageUrlWithResolution(organisation?.logoUrl, "r50x50");
  if (!faviconUrl) return {};

  return {
    icons: {
      icon: [{ url: faviconUrl, sizes: "50x50" }],
      shortcut: [{ url: faviconUrl }],
      apple: [{ url: faviconUrl, sizes: "50x50" }],
    },
  };
};

export function generateStoreMetadata(data) {
  const orgName = data.orgName || "Store";
  const sector = data.sector || "services";
  const description =
    data.description ||
    `${orgName} offers a range of services in the ${sector} sector. Join us to experience top-notch services and stay updated.`;

  const openGraphImages = [];
  if (data.logoUrl) {
    openGraphImages.push({
      url: data.logoUrl,
      width: 800,
      height: 600,
      alt: `${orgName} Logo`,
    });
  }
  if (data.coverPhoto) {
    openGraphImages.push({
      url: data.coverPhoto,
      width: 1200,
      height: 630,
      alt: `${orgName} Cover Photo`,
    });
  }
  const title = getStoreHomeTitle(data);

  return {
    title,
    description,
    openGraph: {
      title,
      description,
      url: data.webLink || "",
      site_name: orgName,
      ...(openGraphImages.length && { images: openGraphImages }),
    },
  twitter: {
      card: "summary_large_image",
      title,
      description,
      ...(data.logoUrl && { images: [data.logoUrl] }),
    },
  };
}

export function generateProductMetadata(product, org) {
  const productName = product.name || "Product";
  const orgName = org.orgName || "Store";
  const description =
    product.description?.trim() ||
    `Buy ${productName} from ${orgName} online. Enjoy great quality at an affordable price.`;

  const images = (product.media || []).filter(
    (media) => media.mediaType === "image",
  );
  const mainImage = images[0]?.mediaThumb || org.logoUrl || "";

  const meta = {
    title: `${productName} | ${orgName}`,
    description,
    openGraph: {
      title: `${productName} - ${orgName}`,
      description,
      ...(mainImage && {
        images: [
          {
            url: mainImage,
            width: 800,
            height: 600,
            alt: productName,
          },
        ],
      }),
    },
    twitter: {
      card: "summary_large_image",
      title: `${productName} - ${orgName}`,
      description,
      ...(mainImage && { images: [mainImage] }),
    },
  };

  return meta;
}

export const getOrganisationTitle = (organisation) =>
  organisation?.orgName || organisation?.name || "CirclePlug";

export const getProductTitle = (urlData) =>
  urlData?.product?.name || urlData?.name;

const capitalizeTitleWords = (value = "") =>
  String(value)
    .trim()
    .split(/\s+/)
    .map((word) => {
      const [firstCharacter, ...rest] = word;
      return firstCharacter
        ? `${firstCharacter.toUpperCase()}${rest.join("").toLowerCase()}`
        : word;
    })
    .join(" ");

export const getStoreHomeTitle = (organisation) => {
  const orgName = organisation?.orgName || "Store";
  const category = organisation?.category || "Business";
  const country = organisation?.country || "";

  return `${capitalizeTitleWords(orgName)} | ${capitalizeTitleWords(category)}${
    country ? ` In ${capitalizeTitleWords(country)}` : ""
  }`;
};

export const getStoreCategoryTitle = ({ organisation, category }) => {
  const categoryName = category?.name;

  if (!categoryName) return getStoreHomeTitle(organisation);

  return `${capitalizeTitleWords(categoryName)} | ${getStoreHomeTitle(
    organisation,
  )}`;
};

export const getStoreViewTitle = ({
  organisation,
  urlPath,
  urlData,
  activeCategory,
}) => {
  const organisationTitle = getOrganisationTitle(organisation);

  if (!organisationTitle || organisationTitle === "CirclePlug") {
    return organisationTitle;
  }

  if (urlPath === "product") {
    const productTitle = getProductTitle(urlData);
    return productTitle
      ? `${capitalizeTitleWords(productTitle)} | ${capitalizeTitleWords(
          organisationTitle,
        )}`
      : capitalizeTitleWords(organisationTitle);
  }

  if (urlPath === "checkout")
    return `Checkout | ${capitalizeTitleWords(organisationTitle)}`;
  if (urlPath === "profile")
    return `Profile | ${capitalizeTitleWords(organisationTitle)}`;
  if (urlPath === "auth")
    return `Sign In | ${capitalizeTitleWords(organisationTitle)}`;

  if (activeCategory) {
    return getStoreCategoryTitle({ organisation, category: activeCategory });
  }

  return getStoreHomeTitle(organisation);
};

export function generateStoreViewMetadata({ organisation, urlPath, urlData }) {
  const productData = urlData?.product || urlData;
  const metadata =
    urlPath === "product" && productData
      ? generateProductMetadata(productData, organisation)
      : generateStoreMetadata(organisation);
  const title = getStoreViewTitle({ organisation, urlPath, urlData });

  return {
    ...metadata,
    ...getStoreIconMetadata(organisation),
    title,
    openGraph: {
      ...metadata.openGraph,
      title,
    },
    twitter: {
      ...metadata.twitter,
      title,
    },
  };
}
